import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Activity, Pause, Play, RefreshCw, TrendingUp, TrendingDown, Zap, Wallet, ChevronLeft } from "lucide-react";

import { API_BASE as API } from '../config';

export default function MobileDashboard() {
  const { token } = useAuth();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cmdLoading, setCmdLoading] = useState(false);
  const [priceDir, setPriceDir] = useState(0);
  const prevPrice = useRef(0);
  const [injectAmt, setInjectAmt] = useState("");
  const [injectResult, setInjectResult] = useState(null);
  const [showInject, setShowInject] = useState(false);
  const [regime, setRegime] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) {
        const data = await r.json();
        setBot(data);
        if (data.current_price && prevPrice.current) {
          setPriceDir(data.current_price > prevPrice.current ? 1 : data.current_price < prevPrice.current ? -1 : 0);
        }
        prevPrice.current = data.current_price || 0;
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchStatus(); const iv = setInterval(fetchStatus, 10000); return () => clearInterval(iv); }, [fetchStatus]);

  // Fetch regime data once
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/eu-bot/regime/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.regime_data) setRegime(d.regime_data); })
      .catch(() => {});
  }, [token]);

  const sendCmd = async (action, params = {}) => {
    setCmdLoading(true);
    try {
      await fetch(`${API}/api/eu-bot/dca/command`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, params }),
      });
      setTimeout(fetchStatus, 2000);
    } catch {}
    setCmdLoading(false);
  };

  const simulateInject = async (amount) => {
    try {
      const r = await fetch(`${API}/api/eu-bot/dca/simulate-injection`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: parseFloat(amount) }),
      });
      if (r.ok) setInjectResult(await r.json());
    } catch {}
  };

  const executeInject = async (amount) => {
    setCmdLoading(true);
    try {
      await fetch(`${API}/api/eu-bot/dca/command`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "manual_buy", params: { amount: parseFloat(amount) } }),
      });
      setInjectResult(null);
      setShowInject(false);
      setTimeout(fetchStatus, 3000);
    } catch {}
    setCmdLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const isConnected = bot?.connected && bot?.timestamp &&
    (Date.now() - new Date(bot.timestamp).getTime()) < 120000;

  // No token = show login prompt
  if (!token) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center px-8 gap-4">
      <span className="text-lg font-mono font-bold text-white">SKYNET</span>
      <span className="text-sm text-zinc-500 font-mono">Login to view dashboard</span>
      <a href="/login" className="px-6 py-3 bg-teal-600 rounded-xl text-sm font-mono font-bold text-black" data-testid="mobile-login-btn">Login</a>
    </div>
  );

  const entry = bot?.avg_entry_price || 0;
  const price = bot?.current_price || 0;
  const tp = bot?.take_profit_pct || 1.5;
  const target = entry * (1 + tp / 100);
  const pnlPct = entry > 0 ? ((price - entry) / entry * 100) : 0;
  const progress = tp > 0 ? Math.max(0, Math.min((pnlPct / tp) * 100, 100)) : 0;
  const estProfit = bot?.total_qty ? (bot.total_qty * target - (bot.total_invested || 0)) : 0;
  const unrealized = (bot?.total_qty || 0) * price - (bot?.total_invested || 0);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="mobile-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <a href="/eu-bot" className="text-zinc-500" data-testid="mobile-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </a>
          <div className="flex items-center gap-2">
            <span className="text-sm font-mono font-bold tracking-tight">SKYNET</span>
            <div className={`w-2 h-2 rounded-full ${isConnected ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`} />
            <span className="text-[10px] font-mono text-zinc-500">
              {isConnected ? (bot?.dry_run ? "DRY" : "LIVE") : "OFF"}
            </span>
            {regime && (() => {
              const colors = { STRONG_BULL: "text-emerald-400 border-emerald-800/50", BULL: "text-cyan-400 border-cyan-800/50", RECOVERY: "text-amber-400 border-amber-800/50", BEAR: "text-red-400 border-red-800/50" };
              return (
                <span className={`px-1.5 py-0.5 rounded text-[8px] font-mono font-bold border ${colors[regime.regime] || "text-zinc-500 border-zinc-700"}`} data-testid="mobile-regime-badge">
                  {regime.regime === "STRONG_BULL" ? "BULL" : regime.regime}
                </span>
              );
            })()}
          </div>
          <button onClick={fetchStatus} className="text-zinc-500 active:text-white" data-testid="mobile-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isConnected ? (
        <div className="flex items-center justify-center h-[60vh] text-center px-8">
          <div>
            <Activity className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
            <div className="text-sm text-zinc-500 font-mono">Bot offline</div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-4 space-y-4">

          {/* Hero PnL in USD */}
          <div className="text-center" data-testid="mobile-price-section">
            <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest mb-1">
              {bot.symbol?.replace("USDC","").replace("USDT","")} &middot; ${price.toFixed(4)}
            </div>
            {bot.in_deal ? (
              <div className={`text-7xl font-mono font-black tracking-tighter leading-none ${unrealized >= 0 ? "text-emerald-400" : "text-red-400"}`}
                data-testid="mobile-pnl-usd">
                {unrealized >= 0 ? "+" : ""}{unrealized.toFixed(2)}$
              </div>
            ) : (
              <div className={`text-4xl font-mono font-bold tracking-tight transition-colors ${
                priceDir > 0 ? "text-emerald-400" : priceDir < 0 ? "text-red-400" : "text-white"
              }`} data-testid="mobile-price">
                ${price.toFixed(4)}
              </div>
            )}
          </div>

          {/* Status Pill + Mood Badge */}
          <div className="flex justify-center items-center gap-2">
            <span className={`px-4 py-1.5 rounded-full text-xs font-mono font-bold border ${
              bot.status === "IN_DEAL" ? "text-amber-400 border-amber-800/40 bg-amber-950/20"
              : bot.status === "PAUSED" ? "text-red-400 border-red-800/40 bg-red-950/20"
              : "text-teal-400 border-teal-800/40 bg-teal-950/20"
            }`} data-testid="mobile-status">
              {bot.status} {bot.trailing_armed ? "| TRAILING" : ""}
            </span>
            {bot.in_deal && (() => {
              const mood = pnlPct >= 1.0 ? { icon: "fire", text: "ON FIRE", cls: "text-emerald-300 bg-emerald-950/40 border-emerald-700/40" }
                : pnlPct >= 0.3 ? { icon: "sun", text: "BULLISH", cls: "text-emerald-400 bg-emerald-950/30 border-emerald-800/30" }
                : pnlPct >= -0.2 ? { icon: "meh", text: "NEUTRAL", cls: "text-zinc-400 bg-zinc-900 border-zinc-700/30" }
                : pnlPct >= -1.0 ? { icon: "cloud", text: "DIP", cls: "text-amber-400 bg-amber-950/30 border-amber-800/30" }
                : { icon: "storm", text: "STORM", cls: "text-red-400 bg-red-950/30 border-red-800/30" };
              const icons = { fire: "🔥", sun: "☀️", meh: "😐", cloud: "🌧", storm: "⛈️" };
              return (
                <span className={`px-3 py-1.5 rounded-full text-xs font-mono font-bold border ${mood.cls}`}
                  data-testid="mobile-mood">
                  {icons[mood.icon]} {mood.text}
                </span>
              );
            })()}
          </div>

          {/* PROFIT BAR — how close to TP */}
          {bot.in_deal && (
            <div data-testid="mobile-progress" className="space-y-3">
              {/* Bar 1: Profit Progress */}
              <div>
                <div className="flex justify-between text-[10px] font-mono mb-1">
                  <span className="text-zinc-600">Entry ${entry.toFixed(4)}</span>
                  <span className={`font-bold ${progress >= 100 ? "text-emerald-400" : "text-teal-500"}`}>TP ${target.toFixed(4)}</span>
                </div>
                <div className="relative h-10 bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800/50">
                  <div className={`absolute inset-y-0 left-0 rounded-2xl transition-all duration-1000 ${
                    progress >= 90 ? "bg-gradient-to-r from-emerald-600 via-emerald-400 to-green-300 animate-pulse"
                    : progress >= 60 ? "bg-gradient-to-r from-teal-600 to-emerald-400"
                    : progress >= 30 ? "bg-gradient-to-r from-teal-700 to-teal-500"
                    : progress > 0 ? "bg-teal-800"
                    : "bg-zinc-800"
                  }`} style={{ width: `${Math.max(progress, 2)}%` }} />
                  <div className="absolute inset-0 flex items-center justify-between px-4">
                    <span className="text-[10px] font-mono text-zinc-400">PROFIT</span>
                    <span className="text-lg font-mono font-black text-white drop-shadow">
                      {progress.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Bar 2: Trailing Status */}
              <div>
                <div className="flex justify-between text-[10px] font-mono mb-1">
                  <span className="text-zinc-600">Trail</span>
                  <span className={`font-bold ${bot.trailing_armed ? "text-emerald-400" : "text-zinc-600"}`}>
                    {bot.trailing_armed ? "LOCKED IN" : `activates @ ${tp}%`}
                  </span>
                </div>
                {(() => {
                  const trailProgress = bot.trailing_armed ? 100 : Math.max(0, Math.min((pnlPct / tp) * 100, 100));
                  const trailDev = bot.trailing_deviation || 0.2;
                  const peakPrice = bot.trailing_peak || 0;
                  const dropFromPeak = peakPrice > 0 ? ((peakPrice - price) / peakPrice * 100) : 0;
                  const dropProgress = bot.trailing_armed ? Math.min((dropFromPeak / trailDev) * 100, 100) : 0;
                  return (
                    <div className="relative h-10 bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800/50">
                      {!bot.trailing_armed ? (
                        <>
                          <div className="absolute inset-y-0 left-0 rounded-2xl transition-all duration-1000 bg-zinc-800"
                            style={{ width: `${Math.max(trailProgress, 2)}%` }} />
                          <div className="absolute inset-0 flex items-center justify-between px-4">
                            <span className="text-[10px] font-mono text-zinc-500">TRAIL</span>
                            <span className="text-sm font-mono font-bold text-zinc-500">waiting...</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="absolute inset-y-0 left-0 rounded-2xl transition-all duration-1000 bg-gradient-to-r from-emerald-600 to-emerald-400 animate-pulse"
                            style={{ width: "100%" }} />
                          {dropProgress > 0 && (
                            <div className="absolute inset-y-0 right-0 rounded-r-2xl transition-all duration-500 bg-red-500/40"
                              style={{ width: `${dropProgress}%` }} />
                          )}
                          <div className="absolute inset-0 flex items-center justify-between px-4">
                            <span className="text-[10px] font-mono text-black font-bold">TRAILING</span>
                            <div className="text-right">
                              <span className="text-sm font-mono font-black text-black">
                                {peakPrice > 0 ? `peak $${peakPrice.toFixed(4)}` : "ACTIVE"}
                              </span>
                              {dropFromPeak > 0 && (
                                <span className="text-[10px] font-mono text-red-900 ml-2">
                                  -{dropFromPeak.toFixed(2)}%
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Key Stats */}
          <div className="grid grid-cols-2 gap-3" data-testid="mobile-stats">
            <div className="bg-zinc-900/80 rounded-xl p-3.5 border border-zinc-800/50">
              <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Equity</div>
              <div className="text-xl font-mono font-bold text-teal-400 mt-1">${(bot.equity || 0).toFixed(0)}</div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl p-3.5 border border-zinc-800/50">
              <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Total PnL</div>
              <div className={`text-xl font-mono font-bold mt-1 ${(bot.total_profit || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {(bot.total_profit || 0) >= 0 ? "+" : ""}${(bot.total_profit || 0).toFixed(2)}
              </div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl p-3.5 border border-zinc-800/50">
              <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Deals</div>
              <div className="text-xl font-mono font-bold text-white mt-1">{bot.deals_completed || 0}</div>
              <div className="text-[10px] text-zinc-600 font-mono">{bot.win_rate || 0}% WR</div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl p-3.5 border border-zinc-800/50">
              <div className="text-[9px] text-zinc-600 uppercase tracking-wider">Safety Orders</div>
              <div className="text-xl font-mono font-bold text-amber-400 mt-1">
                {bot.safety_orders_filled || 0}/{bot.max_safety_orders || 12}
              </div>
            </div>
          </div>

          {/* Deal Info */}
          {bot.in_deal && (
            <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800/50 space-y-3" data-testid="mobile-deal-info">
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-zinc-500 uppercase font-mono">Invested</span>
                <span className="text-sm font-mono font-bold">${(bot.total_invested || 0).toFixed(0)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-zinc-500 uppercase font-mono">Unrealized</span>
                <span className={`text-sm font-mono font-bold ${unrealized >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {unrealized >= 0 ? "+" : ""}${unrealized.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-zinc-500 uppercase font-mono">Est. Profit @ TP</span>
                <span className="text-sm font-mono font-bold text-emerald-400">+${estProfit.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-zinc-500 uppercase font-mono">Qty</span>
                <span className="text-sm font-mono">{(bot.total_qty || 0).toFixed(2)} {bot.symbol?.replace("USDC","").replace("USDT","")}</span>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-2" data-testid="mobile-actions">
            {bot.status === "PAUSED" ? (
              <button onClick={() => sendCmd("resume")} disabled={cmdLoading}
                className="col-span-1 flex flex-col items-center gap-1 py-3 bg-teal-600/15 border border-teal-700/30 rounded-xl active:bg-teal-600/30 disabled:opacity-40"
                data-testid="mobile-btn-resume">
                <Play className="w-5 h-5 text-teal-400" />
                <span className="text-[10px] font-mono text-teal-400">Resume</span>
              </button>
            ) : (
              <button onClick={() => sendCmd("pause")} disabled={cmdLoading}
                className="col-span-1 flex flex-col items-center gap-1 py-3 bg-zinc-900 border border-zinc-800 rounded-xl active:bg-zinc-800 disabled:opacity-40"
                data-testid="mobile-btn-pause">
                <Pause className="w-5 h-5 text-zinc-400" />
                <span className="text-[10px] font-mono text-zinc-400">Pause</span>
              </button>
            )}
            <button onClick={() => sendCmd("sync_balance")} disabled={cmdLoading}
              className="col-span-1 flex flex-col items-center gap-1 py-3 bg-zinc-900 border border-zinc-800 rounded-xl active:bg-zinc-800 disabled:opacity-40"
              data-testid="mobile-btn-sync">
              <Wallet className="w-5 h-5 text-amber-400" />
              <span className="text-[10px] font-mono text-amber-400">Sync $</span>
            </button>
            <button onClick={() => setShowInject(!showInject)}
              className={`col-span-1 flex flex-col items-center gap-1 py-3 rounded-xl border active:opacity-80 ${
                showInject ? "bg-purple-600/20 border-purple-600/40" : "bg-zinc-900 border-zinc-800"
              }`} data-testid="mobile-btn-inject">
              <Zap className={`w-5 h-5 ${showInject ? "text-purple-400" : "text-purple-500"}`} />
              <span className={`text-[10px] font-mono ${showInject ? "text-purple-400" : "text-purple-500"}`}>Inject</span>
            </button>
          </div>

          {/* Injection Panel */}
          {showInject && bot.in_deal && (
            <div className="bg-purple-950/15 border border-purple-800/25 rounded-xl p-4 space-y-3" data-testid="mobile-inject-panel">
              <div className="text-xs font-mono text-purple-400 font-bold">Fund Injection</div>
              <div className="flex gap-2">
                <input type="number" value={injectAmt} onChange={e => setInjectAmt(e.target.value)}
                  placeholder="$ Amount"
                  className="flex-1 bg-black border border-zinc-800 rounded-lg px-3 py-2.5 text-sm font-mono text-white placeholder-zinc-600 focus:border-purple-600 focus:outline-none"
                  data-testid="mobile-inject-input" />
                <button onClick={() => simulateInject(injectAmt)} disabled={!injectAmt}
                  className="px-4 py-2.5 bg-purple-600/20 border border-purple-600/40 text-purple-400 rounded-lg font-mono text-xs active:bg-purple-600/40 disabled:opacity-40"
                  data-testid="mobile-inject-sim-btn">
                  Sim
                </button>
              </div>
              <div className="flex gap-2">
                {[100, 250, 500, 1000].map(a => (
                  <button key={a} onClick={() => { setInjectAmt(String(a)); simulateInject(a); }}
                    className="flex-1 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono text-zinc-400 active:border-purple-700"
                    data-testid={`mobile-inject-q-${a}`}>
                    ${a}
                  </button>
                ))}
              </div>
              {injectResult && !injectResult.error && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-zinc-500">Entry drops</span>
                    <span className="text-purple-400">{injectResult.after_injection?.entry_improvement_pct?.toFixed(3)}%</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-zinc-500">New avg</span>
                    <span className="text-purple-300">${injectResult.after_injection?.new_avg_entry?.toFixed(4)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-zinc-500">Profit @ TP</span>
                    <span className="text-emerald-400 font-bold">${injectResult.after_injection?.est_profit_at_tp?.toFixed(2)}</span>
                  </div>
                  <button onClick={() => executeInject(injectAmt)} disabled={cmdLoading}
                    className="w-full py-3 bg-amber-600 rounded-lg text-sm font-mono font-bold text-black active:bg-amber-500 disabled:opacity-40"
                    data-testid="mobile-inject-execute">
                    {cmdLoading ? "BUYING..." : `BUY $${parseFloat(injectAmt || 0).toFixed(0)} NOW`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Recent Deals */}
          {bot.deal_log_recent?.length > 0 && (
            <div className="space-y-2" data-testid="mobile-recent-deals">
              <div className="text-[10px] text-zinc-600 uppercase font-mono tracking-wider">Recent Deals</div>
              {bot.deal_log_recent.map((d, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 px-3 bg-zinc-900/60 rounded-lg border border-zinc-800/30">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-zinc-600">#{d.deal}</span>
                    <span className={`text-sm font-mono font-bold ${d.profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {d.profit >= 0 ? "+" : ""}${d.profit?.toFixed(2)}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono text-zinc-600">{d.safety_orders} SOs | {d.duration_hours}h</span>
                </div>
              ))}
            </div>
          )}

          <div className="h-8" />
        </div>
      )}
    </div>
  );
}
